import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popper } from '@mui/material';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import {
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuWrap,
} from '@/components/Approval/@components/ActionMenuSurface/styles';
import { Body1, Body2 } from '@/components/@IntranetLibrary/Typography';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import { IS_MY_APPROVAL_LINE_ENABLED } from '@/constant';
import ChevronRightIcon from '@/public/icons/approval/chevron-right.svg';
import CloseIcon from '@/public/icons/approval/close.svg';
import DotsVerticalIcon from '@/public/icons/approval/dots-vertical.svg';
import ProfileIcon from '@/public/icons/approval/profile.svg';
import ProfilesIcon from '@/public/icons/approval/profiles.svg';
import type {
  RecentApprovalLineApprover,
  RecentApprovalLineSummary,
} from './RecentApprovalLineList';
import {
  HistoryDetailApproverItem,
  HistoryDetailApproverWrap,
  HistoryDetailArrowIcon,
  HistoryDetailAvatar,
  HistoryDetailAvatarChip,
  HistoryDetailButtonRow,
  HistoryDetailChipDeactivated,
  HistoryDetailChipText,
  HistoryDetailContents,
  HistoryDetailCoopBox,
  HistoryDetailCoopChips,
  HistoryDetailDivider,
  HistoryDetailMenuAnchor,
  HistoryDetailMenuRoot,
  HistoryDetailPopoverPaper,
  HistoryDetailReferrerWrap,
  HistoryDetailSection,
  HistoryDetailSheetHeader,
  HistoryDetailSheetKebab,
  HistoryDetailSheetTitle,
  HistoryListItemBadgeCooperation,
  HistoryListItemBadgeCooperationText,
  SheetFooter,
  SheetHeader,
  SheetHeaderIconButton,
  SheetHeaderTitle,
} from './styles';

/** 케밥 메뉴 항목이 탭마다 달라 어느 목록에서 열린 팝오버인지 구분 필요 */
export type ApprovalLineSource = 'recent' | 'my';

type RecentApprovalLineDetailProps = {
  item: RecentApprovalLineSummary | null;
  variant: 'popover' | 'drawer';
  /** popover variant에서만 사용 — anchor element */
  anchorEl?: HTMLElement | null;
  lineSource?: ApprovalLineSource;
  onClose: () => void;
  /** '적용하기' — 이 결재라인을 작성 중인 결재라인에 등록 */
  onApply?: (item: RecentApprovalLineSummary) => void;
  /** 퇴사자 조회 대기 중이면 true — 응답 전 적용은 퇴사자 제외를 통째로 건너뜀 */
  applyDisabled?: boolean;
  /** 케밥 > "나의 결재라인에 추가" (최근 결재라인 전용) */
  onAddToMyLines?: (item: RecentApprovalLineSummary) => void;
  /** 케밥 > "결재라인 이름 수정" (나의 결재라인 전용) */
  onRenameLine?: (item: RecentApprovalLineSummary) => void;
  /** 케밥 > "삭제" (나의 결재라인 전용) */
  onDeleteLine?: (item: RecentApprovalLineSummary) => void;
  /** popover paper에 진입 — 호버 close debounce를 cancel */
  onPaperEnter?: () => void;
  /** popover paper에서 이탈 — 호버 close 시작 */
  onPaperLeave?: () => void;
};

const buildTooltip = (a: RecentApprovalLineApprover) => {
  const head = [a.name, a.positionName].filter(Boolean).join(' ');
  return [head, resolveDepartmentName(a, a.departmentName)]
    .filter(Boolean)
    .join(' · ');
};

const AvatarVisual = ({ src, alt }: { src: string | null; alt: string }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }
  return <ProfileIcon width={14} height={14} />;
};

type ChipProps = {
  approver: RecentApprovalLineApprover;
  tone?: 'default' | 'cooperation';
};

const ApproverAvatarChip = ({ approver, tone = 'default' }: ChipProps) => {
  const { t } = useTranslation();
  return (
    <HistoryDetailAvatarChip tone={tone} title={buildTooltip(approver)}>
      <HistoryDetailAvatar aria-hidden>
        <AvatarVisual src={approver.profileImageUrl} alt={approver.name} />
      </HistoryDetailAvatar>
      <HistoryDetailChipText>
        {approver.deactivated && (
          <HistoryDetailChipDeactivated>
            {t('퇴사')}
          </HistoryDetailChipDeactivated>
        )}
        {approver.name}
      </HistoryDetailChipText>
    </HistoryDetailAvatarChip>
  );
};

const ChipSeparator = () => (
  <HistoryDetailArrowIcon aria-hidden>
    <ChevronRightIcon width={10} height={10} />
  </HistoryDetailArrowIcon>
);

/** 결재라인 흐름 안에서 협조 단계 위치를 표시하는 chip — 멤버는 아래 협조 결재 박스에 별도 노출 */
const CooperationStepChip = () => {
  const { t } = useTranslation();
  return (
    <HistoryListItemBadgeCooperation
      surface="popup"
      aria-label={t('협조결재 단계')}
    >
      <ProfilesIcon width={16} height={16} />
      <HistoryListItemBadgeCooperationText>
        {t('협조', { context: 'role' })}
      </HistoryListItemBadgeCooperationText>
    </HistoryListItemBadgeCooperation>
  );
};

type FlowEntry =
  | { kind: 'approver'; approver: RecentApprovalLineApprover }
  | { kind: 'cooperation' };

/** 결재자 chip 사이 cooperationInsertIndex 위치에 협조 chip 을 끼운 시퀀스 */
const buildFlowEntries = (item: RecentApprovalLineSummary): FlowEntry[] => {
  const hasCooperators = (item.cooperators ?? []).length > 0;
  const insertAt = Math.max(
    0,
    Math.min(
      item.cooperationInsertIndex ?? item.approvers.length,
      item.approvers.length,
    ),
  );

  const entries: FlowEntry[] = [];
  item.approvers.forEach((approver, idx) => {
    if (hasCooperators && idx === insertAt) {
      entries.push({ kind: 'cooperation' });
    }
    entries.push({ kind: 'approver', approver });
  });
  if (hasCooperators && insertAt >= item.approvers.length) {
    entries.push({ kind: 'cooperation' });
  }
  return entries;
};

const DetailContents = ({ item }: { item: RecentApprovalLineSummary }) => {
  const { t } = useTranslation();
  const cooperators = item.cooperators ?? [];
  const referrers = item.referrers ?? [];

  return (
    <>
      <HistoryDetailSection>
        <Body1 sx={{ margin: 0 }}>{t('결재라인')}</Body1>
        <HistoryDetailApproverWrap>
          {buildFlowEntries(item).map((entry, idx) => (
            <HistoryDetailApproverItem
              key={
                entry.kind === 'approver'
                  ? entry.approver.passportId
                  : 'cooperation-step'
              }
            >
              {idx > 0 && <ChipSeparator />}
              {entry.kind === 'approver' ? (
                <ApproverAvatarChip approver={entry.approver} />
              ) : (
                <CooperationStepChip />
              )}
            </HistoryDetailApproverItem>
          ))}
        </HistoryDetailApproverWrap>
      </HistoryDetailSection>

      {cooperators.length > 0 && (
        <HistoryDetailCoopBox>
          <Body2
            sx={{
              margin: 0,
              color: 'var(--dk-accent, #0066CC)',
              fontWeight: 500,
            }}
          >
            {t('협조 결재')}
          </Body2>
          <HistoryDetailCoopChips>
            {cooperators.map((c) => (
              <ApproverAvatarChip
                key={c.passportId}
                approver={c}
                tone="cooperation"
              />
            ))}
          </HistoryDetailCoopChips>
        </HistoryDetailCoopBox>
      )}

      {referrers.length > 0 && (
        <HistoryDetailSection>
          <Body1 sx={{ margin: 0 }}>{t('참조인')}</Body1>
          <HistoryDetailReferrerWrap>
            {referrers.map((r) => (
              <ApproverAvatarChip key={r.passportId} approver={r} />
            ))}
          </HistoryDetailReferrerWrap>
        </HistoryDetailSection>
      )}
    </>
  );
};

type HeaderMenuProps = {
  item: RecentApprovalLineSummary;
  lineSource: ApprovalLineSource;
  surface: 'popover' | 'drawer';
  onAddToMyLines?: (item: RecentApprovalLineSummary) => void;
  onRenameLine?: (item: RecentApprovalLineSummary) => void;
  onDeleteLine?: (item: RecentApprovalLineSummary) => void;
  onOpenChange?: (open: boolean) => void;
};

const HeaderMenu = ({
  item,
  lineSource,
  surface,
  onAddToMyLines,
  onRenameLine,
  onDeleteLine,
  onOpenChange,
}: HeaderMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const setOpenState = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && anchorRef.current?.contains(target)) return;
      setOpenState(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenState(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpenState]);

  const run = (action?: (item: RecentApprovalLineSummary) => void) => () => {
    setOpenState(false);
    action?.(item);
  };

  const isDrawer = surface === 'drawer';
  const KebabButton = isDrawer
    ? SheetHeaderIconButton
    : HistoryDetailSheetKebab;
  const kebabIconSize = isDrawer ? 20 : 16;

  return (
    <HistoryDetailMenuAnchor ref={anchorRef}>
      <KebabButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('{{title}} 결재라인 메뉴', { title: item.title })}
        onClick={() => setOpenState(!open)}
      >
        <DotsVerticalIcon width={kebabIconSize} height={kebabIconSize} />
      </KebabButton>
      {open && (
        <HistoryDetailMenuRoot role="menu" align={isDrawer ? 'left' : 'right'}>
          <ActionMenuWrap>
            <ActionMenuContent>
              {lineSource === 'recent' ? (
                <ActionMenuItem
                  type="button"
                  role="menuitem"
                  onClick={run(onAddToMyLines)}
                >
                  {t('나의 결재라인에 추가')}
                </ActionMenuItem>
              ) : (
                <>
                  <ActionMenuItem
                    type="button"
                    role="menuitem"
                    onClick={run(onRenameLine)}
                  >
                    {t('결재라인 이름 수정')}
                  </ActionMenuItem>
                  <ActionMenuItem
                    type="button"
                    role="menuitem"
                    tone="caution"
                    onClick={run(onDeleteLine)}
                  >
                    {t('삭제')}
                  </ActionMenuItem>
                </>
              )}
            </ActionMenuContent>
          </ActionMenuWrap>
        </HistoryDetailMenuRoot>
      )}
    </HistoryDetailMenuAnchor>
  );
};

type DetailFooterProps = {
  item: RecentApprovalLineSummary;
  applyDisabled?: boolean;
  onApply?: (item: RecentApprovalLineSummary) => void;
};

const ApplyButton = ({
  item,
  size,
  applyDisabled,
  onApply,
}: DetailFooterProps & { size: 'medium' | 'large' }) => {
  const { t } = useTranslation();
  return (
    <BoxButton
      variant="secondary"
      size={size}
      disabled={applyDisabled}
      onClick={() => onApply?.(item)}
      aria-label={t('{{title}} 결재라인 적용하기', { title: item.title })}
    >
      {t('적용하기')}
    </BoxButton>
  );
};

/** 최근/나의 결재라인 디테일 popup — 데스크탑은 카드 아래 Popper, 모바일은 bottom drawer */
const RecentApprovalLineDetail = ({
  item,
  variant,
  anchorEl,
  lineSource = 'recent',
  onClose,
  onApply,
  applyDisabled,
  onAddToMyLines,
  onRenameLine,
  onDeleteLine,
  onPaperEnter,
  onPaperLeave,
}: RecentApprovalLineDetailProps) => {
  const { t } = useTranslation();
  /** 메뉴 조작 중 paper 이탈은 무시 — 커서가 헤더 밖으로 스치면 팝오버째 닫히는 것 방지 */
  const menuOpenRef = useRef(false);
  /** 메뉴가 닫히는 시점의 커서 위치 판단용 — 밖이면 mouseleave 가 다시 오지 않아 직접 닫아야 함 */
  const pointerInsideRef = useRef(false);

  const handlePaperEnter = () => {
    pointerInsideRef.current = true;
    onPaperEnter?.();
  };

  const handlePaperLeave = () => {
    pointerInsideRef.current = false;
    if (menuOpenRef.current) return;
    onPaperLeave?.();
  };

  const handleMenuOpenChange = (open: boolean) => {
    menuOpenRef.current = open;
    if (open) {
      onPaperEnter?.();
      return;
    }
    if (!pointerInsideRef.current) onPaperLeave?.();
  };

  if (!item) return null;

  // 최근 결재라인 메뉴 항목은 '나의 결재라인에 추가' 하나뿐 — 플래그 OFF 면 빈 메뉴가 되므로 케밥째 미노출
  const hasMenuItems = lineSource === 'my' || IS_MY_APPROVAL_LINE_ENABLED;

  const headerMenu = hasMenuItems ? (
    <HeaderMenu
      item={item}
      lineSource={lineSource}
      surface={variant}
      onAddToMyLines={onAddToMyLines}
      onRenameLine={onRenameLine}
      onDeleteLine={onDeleteLine}
      onOpenChange={variant === 'popover' ? handleMenuOpenChange : undefined}
    />
  ) : null;

  if (variant === 'popover') {
    return (
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            options: { padding: 8, altAxis: true },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <HistoryDetailPopoverPaper
          onMouseEnter={handlePaperEnter}
          onMouseLeave={handlePaperLeave}
        >
          <HistoryDetailSheetHeader>
            <HistoryDetailSheetTitle title={item.title}>
              {item.title}
            </HistoryDetailSheetTitle>
            {headerMenu}
          </HistoryDetailSheetHeader>
          <HistoryDetailContents>
            <DetailContents item={item} />
          </HistoryDetailContents>
          <HistoryDetailDivider />
          <HistoryDetailButtonRow>
            <ApplyButton
              item={item}
              size="medium"
              applyDisabled={applyDisabled}
              onApply={onApply}
            />
          </HistoryDetailButtonRow>
        </HistoryDetailPopoverPaper>
      </Popper>
    );
  }

  return (
    // 케밥·ellipsis 제목이 있는 자체 SheetHeader 를 유지해야 해서 title prop 대신
    // 내용물이 헤더를 직접 그린다 (FileViewer AI 패널과 같은 패턴).
    // safe-area 하단 패딩은 footer 슬롯(DrawerFooter)이 담당한다.
    <MobileBottomSheet
      open={Boolean(item)}
      onClose={onClose}
      height="auto"
      showHandle={false}
      swipeable={false}
      footer={
        <SheetFooter>
          <ApplyButton
            item={item}
            size="large"
            applyDisabled={applyDisabled}
            onApply={onApply}
          />
        </SheetFooter>
      }
    >
      <SheetHeader>
        {/* 케밥이 없을 때도 제목이 가운데 오도록 빈 슬롯 유지 */}
        {headerMenu ?? <span />}
        <SheetHeaderTitle title={item.title}>{item.title}</SheetHeaderTitle>
        <SheetHeaderIconButton
          type="button"
          onClick={onClose}
          aria-label={t('닫기')}
        >
          <CloseIcon width={16} height={16} />
        </SheetHeaderIconButton>
      </SheetHeader>
      <HistoryDetailContents compact>
        <DetailContents item={item} />
      </HistoryDetailContents>
    </MobileBottomSheet>
  );
};

export default RecentApprovalLineDetail;
